import { useState, useCallback, useRef } from "react";
import { v4 as uuidV4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    CreateTemplateInstanceValidation,
    useCreateCfrBulkSendBatch,
    useAppendCfrBulkSendBatchItems,
    useFinalizeCfrBulkSendBatch,
} from "@/lib/api/cfr";
import { chunkArray } from "@/lib/prelude";
import { RoutePath } from "@/section/router";

/**
 * Shared submit-flow hook for all five CFR bulk-send dialogs (spec
 * \u00a72/\u00a77). Each dialog builds its own list of would-be
 * `CreateTemplateInstanceValidation` payloads and hands them to
 * `submit(...)`. This hook drives the three-step lifecycle:
 *
 *   1. create batch
 *   2. append items in chunks (default 10/chunk)
 *   3. finalize
 *
 * On any step's failure: surfaces the upstream `message` via the
 * `phase: "error"` state so the dialog can show it verbatim (spec
 * \u00a77.3 \u2014 no toast collapsing).
 *
 * On success of all three steps: invalidates the cfr query cache and
 * navigates to the batch status page.
 *
 * Returns `{ phase, isSubmitting, submit, close }`. `submit` returns
 * `true` on success and `false` on failure so the dialog can keep
 * itself open in the error state.
 */

export type BulkSendBatchPhase =
    | { kind: "idle" }
    | { kind: "creating" }
    | { kind: "uploading"; currentChunk: number; totalChunks: number; uploadedItems: number; totalItems: number }
    | { kind: "finalizing" }
    | { kind: "error"; phase: "creating" | "uploading" | "finalizing" | "local_validation"; message: string };

export type BulkSendInstancePayload = typeof CreateTemplateInstanceValidation._type;

export interface UseBulkSendBatchSubmitterOptions {
    /** Items per append-items HTTP request. Default 10. */
    appendChunkSize?: number;
    /**
     * Called once the redirect would fire. Most dialogs just want to
     * close themselves \u2014 if you don't pass anything, the hook calls
     * navigate directly after returning success. Use this if you also
     * want to reset local form state.
     */
    onBeforeRedirect?: (batch_id: string) => void;
}

export interface UseBulkSendBatchSubmitterResult {
    phase: BulkSendBatchPhase;
    isSubmitting: boolean;
    submit: (payloads: BulkSendInstancePayload[]) => Promise<boolean>;
    /** Reset to idle. Safe to call from a Close button in the error state. */
    close: () => void;
}

const DEFAULT_APPEND_CHUNK_SIZE = 10;

export function useBulkSendBatchSubmitter(
    options: UseBulkSendBatchSubmitterOptions = {},
): UseBulkSendBatchSubmitterResult {
    const { appendChunkSize = DEFAULT_APPEND_CHUNK_SIZE, onBeforeRedirect } = options;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const createBatch = useCreateCfrBulkSendBatch();
    const appendItems = useAppendCfrBulkSendBatchItems();
    const finalizeBatch = useFinalizeCfrBulkSendBatch();

    const [phase, setPhase] = useState<BulkSendBatchPhase>({ kind: "idle" });

    const isSubmitting = phase.kind !== "idle" && phase.kind !== "error";

    // Synchronous lock to prevent a double-submit racing the state
    // update from setPhase. `phase` is async (setState), so two clicks
    // in the same frame could both observe `isSubmitting === false`.
    // The ref check happens before any awaits.
    const submitLockRef = useRef(false);

    const close = useCallback(() => {
        // Caller is responsible for guarding against close-during-work.
        submitLockRef.current = false;
        setPhase({ kind: "idle" });
    }, []);

    // Intentionally NOT useCallback. submit is rebuilt on every render
    // so its closure always sees the latest createBatch/appendItems/
    // finalizeBatch/navigate/queryClient/onBeforeRedirect from the
    // current render. Referential stability would be a footgun here:
    // submit is only called from the dialog's onSubmit handler and is
    // not used as a dep anywhere, so there's nothing to gain from
    // memoizing it. Double-submit protection is the synchronous ref
    // lock above, not memoization.
    const submit = async (payloads: BulkSendInstancePayload[]): Promise<boolean> => {
        if (submitLockRef.current) { return false; }
        submitLockRef.current = true;
        try {
            return await runSubmit(payloads);
        } finally {
            submitLockRef.current = false;
        }
    };

    // Inner body kept separate so the lock-acquire/release is one tight
    // try/finally and the body can `return` freely without leaking the
    // lock.
    const runSubmit = async (payloads: BulkSendInstancePayload[]): Promise<boolean> => {

        if (payloads.length === 0) {
            setPhase({ kind: "error", phase: "local_validation", message: "No items to send" });
            return false;
        }

        // ----- Build staged items + local zod re-validation -----
        const stagedItems: Array<{ client_item_id: string; payload: BulkSendInstancePayload }> = [];
        for (const payload of payloads) {
            const parsed = CreateTemplateInstanceValidation.safeParse(payload);
            if (!parsed.success) {
                setPhase({
                    kind: "error",
                    phase: "local_validation",
                    message: `Local payload validation failed: ${parsed.error.message}`,
                });
                return false;
            }
            stagedItems.push({ client_item_id: uuidV4(), payload: parsed.data });
        }

        // ----- Phase 1: create batch -----
        setPhase({ kind: "creating" });
        const createResult = await createBatch({ expected_item_count: stagedItems.length });
        if (!createResult.success) {
            setPhase({
                kind: "error",
                phase: "creating",
                message: createResult.message || "Failed to create bulk send batch",
            });
            return false;
        }
        const batch_id = createResult.data.batch_id;

        // ----- Phase 2: upload items in chunks -----
        const chunks = chunkArray(appendChunkSize, stagedItems);
        let uploaded = 0;
        for (let i = 0; i < chunks.length; i += 1) {
            const chunk = chunks[i];
            setPhase({
                kind: "uploading",
                currentChunk: i + 1,
                totalChunks: chunks.length,
                uploadedItems: uploaded,
                totalItems: stagedItems.length,
            });
            const appendResult = await appendItems({ batch_id, items: chunk });
            if (!appendResult.success) {
                setPhase({
                    kind: "error",
                    phase: "uploading",
                    message: appendResult.message || `Failed to upload items chunk ${i + 1} of ${chunks.length}`,
                });
                return false;
            }
            uploaded += chunk.length;
        }

        // ----- Phase 3: finalize -----
        setPhase({ kind: "finalizing" });
        const finalizeResult = await finalizeBatch({ batch_id });
        if (!finalizeResult.success) {
            setPhase({
                kind: "error",
                phase: "finalizing",
                message: finalizeResult.message || "Failed to finalize bulk send batch",
            });
            return false;
        }

        // ----- Success: invalidate, hand off, redirect -----
        queryClient.invalidateQueries({ queryKey: ["cfr"] });
        if (onBeforeRedirect) { onBeforeRedirect(batch_id); }
        setPhase({ kind: "idle" });
        navigate(RoutePath.CfrBulkSendBatch.replace(":batchId", batch_id));
        return true;
    };

    return { phase, isSubmitting, submit, close };
}
